import { BACKGROUND_PRESETS } from '@praesto/shared'
import type { CameraStore } from '../canvas/CameraStore.js'
import { useText } from '../i18n.js'
import { useKeepInView } from './keepInView.js'
import { usePopover } from './usePopover.js'

interface Props {
  value: string
  onChange: (color: string) => void
  /** Hausfarben des Boards; stehen immer an erster Stelle. */
  palette?: readonly string[]
  /** Erlaubt „ohne Farbe" — für Flächen, die durchsichtig bleiben dürfen. */
  allowNone?: boolean
  /** Auf dem Board sitzt der Wähler im skalierten Layer und rechnet den Zoom heraus. */
  scaled?: boolean
  /** Nur auf dem Board: nötig, damit das Feld beim Zoomen im Fenster bleibt. */
  camera?: CameraStore
  title?: string
}

/**
 * Farbwähler mit Hausfarben, Vorschlägen und freier Wahl.
 *
 * Überall dieselbe Bedienung: erst die Farben dieses Boards, dann neutrale
 * Vorschläge, und zuletzt der Systemwähler für alles andere. Ohne den letzten
 * Schritt wäre jede kuratierte Palette irgendwann im Weg.
 */
export function ColorPicker({
  value,
  onChange,
  palette = [],
  allowNone = false,
  scaled = false,
  camera,
  title,
}: Props): React.ReactElement {
  const t = useText()
  const { open, toggle, ref: rootRef } = usePopover<HTMLDivElement>()
  const popoverRef = useKeepInView<HTMLDivElement>(camera)

  const suggestions = BACKGROUND_PRESETS.map((preset) => preset.value).filter(
    (color) => !palette.includes(color),
  )

  return (
    <div
      className={`color-picker${scaled ? ' is-scaled' : ''}`}
      ref={rootRef}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className={`color-trigger${value === '' ? ' is-none' : ''}`}
        style={value ? { backgroundColor: value } : undefined}
        onClick={toggle}
        title={title ?? t('color.pick')}
        aria-expanded={open}
      />

      {/*
        Früher lag hinter dem geöffneten Feld eine unsichtbare Fangfläche: Das
        aufgeklappte Feld legt sich über die benachbarten Wähler, ein Klick
        dorthin sollte nur schließen statt eine fremde Farbe zu treffen. Das
        besorgt jetzt der gemeinsame Riegel — er lässt ohnehin nie zwei Menüs
        gleichzeitig offen, und ein Klick auf einen Nachbarn wechselt direkt
        dorthin, statt einen Klick zu verschlucken.
      */}
      {open && (
        <div className="color-popover" ref={popoverRef}>
          {palette.length > 0 && (
            <>
              <span className="color-group-label">{t('color.boardColors')}</span>
              <div className="color-row">
                {palette.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`color-dot${value === color ? ' is-on' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => onChange(color)}
                    title={color}
                  />
                ))}
              </div>
            </>
          )}

          <span className="color-group-label">{t('color.suggestions')}</span>
          <div className="color-row">
            {allowNone && (
              <button
                type="button"
                className={`color-dot is-none${value === '' ? ' is-on' : ''}`}
                onClick={() => onChange('')}
                title={t('color.none')}
              />
            )}
            {suggestions.map((color) => (
              <button
                key={color}
                type="button"
                className={`color-dot${value === color ? ' is-on' : ''}`}
                style={{ backgroundColor: color }}
                onClick={() => onChange(color)}
                title={color}
              />
            ))}
          </div>

          <label className="color-custom">
            <span>{t('color.custom')}</span>
            <input
              type="color"
              value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : '#ffffff'}
              onChange={(event) => onChange(event.target.value)}
            />
          </label>
        </div>
      )}
    </div>
  )
}
